/**
 * Help Center Illustration Registry
 *
 * Type-safe registry for SVG illustrations used in help articles.
 * Uses lazy loading for performance optimization.
 */

import { lazy, ComponentType } from 'react';

/** Props passed to all illustration components */
export interface IllustrationProps {
  /** Optional CSS class name */
  className?: string;
  /** Width in pixels or CSS value */
  width?: number | string;
  /** Height in pixels or CSS value */
  height?: number | string;
  /** Accessible label for screen readers */
  ariaLabel?: string;
}

/** Available illustration identifiers */
export type IllustrationName =
  | 'VelocityFlow'
  | 'AlertThresholdDiagram'
  | 'CloudflareArchitecture'
  | 'NotificationFlow'
  | 'SeasonalPatternCycle'
  | 'InventoryLifecycle'
  | 'DataSyncProcess'
  | 'AlertSeverityLevels'
  | 'LookbackPeriodComparison'
  | 'DigestScheduleTimeline'
  | 'WebhookIntegrationFlow';

/** Illustration metadata */
export interface IllustrationMeta {
  /** Human-readable title */
  title: string;
  /** Brief description for accessibility */
  description: string;
  /** Recommended aspect ratio (width:height) */
  aspectRatio: string;
  /** Default dimensions */
  defaultWidth: number;
  defaultHeight: number;
}

/** Registry of illustration metadata */
export const illustrationMeta: Record<IllustrationName, IllustrationMeta> = {
  VelocityFlow: {
    title: 'Velocity Calculation Flow',
    description: 'How daily sales velocity is calculated from historical data',
    aspectRatio: '16:9',
    defaultWidth: 640,
    defaultHeight: 360,
  },
  AlertThresholdDiagram: {
    title: 'Alert Threshold Levels',
    description: 'Warning, urgent, and critical threshold visualization',
    aspectRatio: '4:3',
    defaultWidth: 480,
    defaultHeight: 360,
  },
  CloudflareArchitecture: {
    title: 'Cloudflare Workers Architecture',
    description: 'Request flow through Workers, D1, KV, and cron triggers',
    aspectRatio: '16:9',
    defaultWidth: 640,
    defaultHeight: 360,
  },
  NotificationFlow: {
    title: 'Notification Delivery Flow',
    description: 'How alerts flow through notification channels',
    aspectRatio: '4:3',
    defaultWidth: 480,
    defaultHeight: 360,
  },
  SeasonalPatternCycle: {
    title: 'Seasonal Pattern Learning',
    description: 'Monthly multiplier adjustment cycle',
    aspectRatio: '1:1',
    defaultWidth: 400,
    defaultHeight: 400,
  },
  InventoryLifecycle: {
    title: 'Inventory Monitoring Lifecycle',
    description: 'From stock tracking to alert generation',
    aspectRatio: '16:9',
    defaultWidth: 640,
    defaultHeight: 360,
  },
  DataSyncProcess: {
    title: 'Shopify Data Sync',
    description: 'Webhook and API sync process',
    aspectRatio: '4:3',
    defaultWidth: 480,
    defaultHeight: 360,
  },
  AlertSeverityLevels: {
    title: 'Alert Severity Levels',
    description: 'Visual guide to warning, urgent, and critical alerts',
    aspectRatio: '3:1',
    defaultWidth: 600,
    defaultHeight: 200,
  },
  LookbackPeriodComparison: {
    title: 'Lookback Period Comparison',
    description: 'Comparing 7, 14, 30, and 90 day lookback periods',
    aspectRatio: '16:9',
    defaultWidth: 640,
    defaultHeight: 360,
  },
  DigestScheduleTimeline: {
    title: 'Digest Schedule Timeline',
    description: 'Immediate, hourly, and daily notification timing',
    aspectRatio: '4:1',
    defaultWidth: 640,
    defaultHeight: 160,
  },
  WebhookIntegrationFlow: {
    title: 'Webhook Integration Flow',
    description: 'How alerts trigger webhook POST requests to external endpoints',
    aspectRatio: '4:1',
    defaultWidth: 600,
    defaultHeight: 150,
  },
};

/** Lazy-loaded illustration components */
export const illustrations: Record<
  IllustrationName,
  ComponentType<IllustrationProps>
> = {
  VelocityFlow: lazy(() => import('./VelocityFlow')),
  AlertThresholdDiagram: lazy(() => import('./AlertThresholdDiagram')),
  CloudflareArchitecture: lazy(() => import('./CloudflareArchitecture')),
  NotificationFlow: lazy(() => import('./NotificationFlow')),
  SeasonalPatternCycle: lazy(() => import('./SeasonalPatternCycle')),
  InventoryLifecycle: lazy(() => import('./InventoryLifecycle')),
  DataSyncProcess: lazy(() => import('./DataSyncProcess')),
  AlertSeverityLevels: lazy(() => import('./AlertSeverityLevels')),
  LookbackPeriodComparison: lazy(() => import('./LookbackPeriodComparison')),
  DigestScheduleTimeline: lazy(() => import('./DigestScheduleTimeline')),
  WebhookIntegrationFlow: lazy(() => import('./WebhookIntegrationFlow')),
};

/** Type guard to check if a string is a valid illustration name */
export function isIllustrationName(name: string): name is IllustrationName {
  return name in illustrations;
}

/** Get illustration component by name (returns undefined if not found) */
export function getIllustration(
  name: string
): ComponentType<IllustrationProps> | undefined {
  if (isIllustrationName(name)) {
    return illustrations[name];
  }
  return undefined;
}

/** Get illustration metadata by name */
export function getIllustrationMeta(
  name: string
): IllustrationMeta | undefined {
  if (isIllustrationName(name)) {
    return illustrationMeta[name];
  }
  return undefined;
}
